import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Rating } from '@mui/material'
import { getCurrentCsatTool } from '../../../feedback/csatTool'
import { trackCsatOpened, trackCsatDismissed, trackCsatSubmitted, identifyCsatResponse } from '../../../feedback/csatTracking'
import { recordCsatResponse } from '../../../feedback/csatStorage'
import { CSAT_SOURCE_PASSIVE_LINK } from '../../../feedback/constants'
import { csatTheme } from '../../../feedback/csatTheme'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 32px 24px;
  text-align: center;
  font-family: 'Raleway', sans-serif;
  background: ${csatTheme.surface};
`

const Title = styled.h2`
  color: ${csatTheme.text};
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 22px;
  margin: 0 0 6px;
`

const Subtitle = styled.p`
  color: ${csatTheme.textMuted};
  font-size: 14px;
  margin: 0 0 18px;
`

const StyledRating = styled(Rating)`
  font-size: 44px !important;
  margin-bottom: 4px;

  & .MuiRating-iconFilled {
    color: ${csatTheme.teal};
  }
  & .MuiRating-iconHover {
    color: ${csatTheme.cyan};
  }
  & .MuiRating-iconEmpty {
    color: ${csatTheme.starEmpty};
  }
`

const FieldLabel = styled.label`
  align-self: flex-start;
  color: ${csatTheme.label};
  font-size: 13px;
  font-weight: 500;
  margin: 16px 0 6px;
`

const fieldStyles = css`
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${csatTheme.inputBorder};
  background: ${csatTheme.inputBg};
  padding: 10px 12px;
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  color: ${csatTheme.text};
  outline: none;

  &::placeholder {
    color: ${csatTheme.placeholder};
  }

  &:focus {
    border-color: ${csatTheme.teal};
    box-shadow: 0 0 0 2px ${csatTheme.teal}33;
  }
`

const TextInput = styled.input`
  ${fieldStyles}
`

const CommentInput = styled.textarea`
  ${fieldStyles}
  resize: vertical;
`

const ErrorText = styled.span`
  align-self: flex-start;
  color: ${csatTheme.error};
  font-size: 13px;
  margin-top: 8px;
`

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
  margin-top: 24px;
`

const buttonStyles = css`
  border: none;
  border-radius: ${csatTheme.radius};
  padding: 10px 22px;
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`

const SubmitButton = styled.button`
  ${buttonStyles}
  background: ${csatTheme.ctaGradient};
  color: ${csatTheme.ctaText};
`

const CancelButton = styled.button`
  ${buttonStyles}
  background: transparent;
  color: ${csatTheme.text};
  border: 1px solid ${csatTheme.cancelBorder};
`

const MAX_COMMENT_LENGTH = 1000
const MAX_CONTACT_LENGTH = 254

// Presentational CSAT modal. Receives `t` + `closeModal` from ModalBase and `userId` +
// `showSuccessNotification` from its container. Owns the form-interaction tracking (open on
// mount, dismiss on close-without-submit, submit on send) so the feature stays self-contained.
// Dark theme drawn from the Layers Crop marketing palette (see feedback/csatTheme.js).
export default function CsatModal({ t, closeModal, userId, showSuccessNotification }) {
  const tool = useMemo(() => getCurrentCsatTool(), [])
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [contact, setContact] = useState('')
  const [error, setError] = useState(null)
  const hasSubmittedRef = useRef(false)

  useEffect(() => {
    trackCsatOpened({ tool, source: CSAT_SOURCE_PASSIVE_LINK })
  }, [tool])

  const handleClose = () => {
    if (!hasSubmittedRef.current) trackCsatDismissed({ tool })
    closeModal()
  }

  const handleRatingChange = (event, value) => {
    setRating(value || 0)
    if (value) setError(null)
  }

  const handleSubmit = () => {
    if (!rating) {
      setError('Please select a rating')
      return
    }
    const at = new Date().toISOString()
    const trimmedComment = comment.trim()
    const trimmedContact = contact.trim()

    hasSubmittedRef.current = true
    trackCsatSubmitted({ rating, tool, comment: trimmedComment || undefined, contact: trimmedContact || undefined })
    recordCsatResponse(userId, { rating, tool, at })
    identifyCsatResponse(userId, { at, tool })

    if (showSuccessNotification) showSuccessNotification('Thanks for your feedback!')
    closeModal()
  }

  return (
    <Wrapper data-testid="csat-modal">
      <Title>{t('How would you rate your experience?')}</Title>
      <Subtitle>{t('Your feedback helps us improve Layers')}</Subtitle>

      <StyledRating name="csat-rating" value={rating} onChange={handleRatingChange} size="large" />
      {error && <ErrorText data-testid="csat-error">{t(error)}</ErrorText>}

      <FieldLabel htmlFor="csat-contact">{t('Name or email (optional)')}</FieldLabel>
      <TextInput
        id="csat-contact"
        data-testid="csat-contact"
        type="text"
        maxLength={MAX_CONTACT_LENGTH}
        value={contact}
        placeholder="name@example.com"
        onChange={event => setContact(event.target.value)}
      />

      <FieldLabel htmlFor="csat-comment">{t('Additional comments (optional)')}</FieldLabel>
      <CommentInput
        id="csat-comment"
        data-testid="csat-comment"
        rows={4}
        maxLength={MAX_COMMENT_LENGTH}
        value={comment}
        placeholder={t('Tell us what we can do better')}
        onChange={event => setComment(event.target.value)}
      />

      <ButtonsRow>
        <CancelButton type="button" onClick={handleClose}>
          {t('cancel')}
        </CancelButton>
        <SubmitButton type="button" onClick={handleSubmit}>
          {t('Submit feedback')}
        </SubmitButton>
      </ButtonsRow>
    </Wrapper>
  )
}
